import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { isMobile } from 'react-device-detect';
import moment from 'moment';

import ApiService from 'services/ApiService/ApiService';
import CookieStorageService from 'services/CookieStorageService';
import { translate } from 'services/i18nService';
import {
  CHECK_MODAL,
  CHECK_MODAL_TYPE,
  CONSENT_URL_LIST,
  CONSENT_URL,
} from 'pages/Settings/AccountSettings/Privacy/Api';
import Button from 'components/Button/Button';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { USER_MODALS_GROUP } from 'enum/helpers';
import routes from 'enum/routeDefinitions';
import { generateID } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';

import styles from './CookieConsent.css';


const APPROVED = 'APPROVED';
const COOKIES = 'COOKIES';
const OK = 'OK';

class CookieConsent extends PureComponent {
  static propTypes = {
    handleQuestionsNotifications: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
  };

  constructor(...args) {
    super(...args);

    this.state = {
      allowCookies: '',
      allowCookiesUpdate: false,
      dateUpdate: '',
    };

    this.IDS = {
      prefix: 'B0lLaRIC',
      element: 'cookieConsent',
      buttonUpdate: 'consentCookieBtnUpdate',
      button: 'consentCookieBtn',
      cookieLink: 'cookie-link',
      settingsLink: 'settings-link',
    };
    this.makeId = generateID(this.IDS.prefix, this.IDS.element);
  }

  async componentDidMount() {
    const { user: { username, accessLevel } } = this.props;
    const isGuestBilling = accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;

    if (username && !isGuestBilling) {
      await this.checkShownCookiesUpdated();
      this.fetchConsent();
    }
  }

  async componentDidUpdate(prevProps) {
    const { user: { username, accessLevel } } = this.props;
    const isGuestBilling = accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;

    if (prevProps.user.username !== username && username && !isGuestBilling) {
      await this.checkShownCookiesUpdated();
      this.fetchConsent();
    }
  }

  checkShownCookiesUpdated = () => {
    const { user: { username } } = this.props;

    const url = ApiService.substitute(CHECK_MODAL_TYPE, {
      username: encodeURIComponent(username),
      modalType: encodeURIComponent(USER_MODALS_GROUP.COOKIES_POLICY_UPDATED),
    });

    return ApiService.get(url)
      .then((res) => {
        if (res?.data && res?.data?.length) {
          this.setState({ dateUpdate: res.data[0].updatedAt });
        } else {
          this.setState({ dateUpdate: '' });
        }
      })
      .catch((e) => parseCatch(e));
  };

  fetchConsent = () => {
    const { dateUpdate } = this.state;
    const { user: { username } } = this.props;

    const url = ApiService.substitute(
      CONSENT_URL_LIST,
      { username: encodeURIComponent(username) }
    );
    ApiService.get(url)
      .then((res) => {
        const dataUpdated = res?.data?.find((con) => con.consent.consentType === COOKIES);
        this.setState({ allowCookies: dataUpdated.userConsentStatus });

        const userApproved = dataUpdated.userConsentStatus;
        const userConsentCreatedAt = moment(dataUpdated.userConsentCreatedAt);
        const updatedAt = moment(dataUpdated.consent.updatedAt);
        if (updatedAt > userConsentCreatedAt && userApproved === APPROVED) {
          if (moment(dateUpdate) < userConsentCreatedAt || dateUpdate === '') {
            this.setState({ allowCookiesUpdate: true });
          } else {
            this.setState({ allowCookiesUpdate: false });
          }
        } else {
          this.setState({ allowCookiesUpdate: false });
        }
      })
      .catch((err) => {
        parseCatch(err);
      });
  };

  updateOption = () => {
    const { user: { username } } = this.props;
    const data = [{
      consentType: COOKIES,
      userConsentStatus: APPROVED,
    }];
    const url = ApiService.substitute(
      CONSENT_URL,
      { username: encodeURIComponent(username) }
    );

    return ApiService.post(url, { data })
      .then(() => this.setState({ allowCookies: APPROVED }))
      .catch((err) => {
        parseCatch(err);
      });
  };

  onAcceptCookiesUpdate = () => {
    const { handleQuestionsNotifications, user } = this.props;
    const isGuestBilling = user.user.getIn(['user', 'accessLevel']) === ACCESS_LEVEL_NUM.GUEST_BILLING;

    this.setState(
      { shouldDisplayUpdate: false },
      () => handleQuestionsNotifications()
    );

    if (user.username && !isGuestBilling) {
      const url = ApiService.substitute(
        CHECK_MODAL,
        { username: encodeURIComponent(user.username) }
      );
      const data = {
        modalType: USER_MODALS_GROUP.COOKIES_POLICY_UPDATED,
        action: OK,
      };
      ApiService.post(url, { data })
        .then(() => {
          this.setState({ allowCookiesUpdate: false });
        })
        .catch((e) => {
          parseCatch(e);
        });
    }
  };

  onAcceptCookies = () => {
    const { handleQuestionsNotifications, user } = this.props;

    this.setState(
      { shouldDisplay: false },
      () => handleQuestionsNotifications()
    );

    const isGuestBilling = user.user.getIn(['user', 'accessLevel']) === ACCESS_LEVEL_NUM.GUEST_BILLING;
    if (user.username && !isGuestBilling) {
      this.updateOption();
      this.setState({ allowCookies: APPROVED });
    } else {
      CookieStorageService.set('CAM4AcceptCookieConsent', true, { path: '/' });
    }
  };

  render() {
    const { user } = this.props;

    let shouldDisplay = undefined;
    let shouldDisplayUpdate = undefined;
    const isGuestBilling = user.user.getIn(['user', 'accessLevel']) === ACCESS_LEVEL_NUM.GUEST_BILLING;
    if (user.username && !isGuestBilling) {
      shouldDisplay = this.state.allowCookies !== APPROVED && !isMobile;
      shouldDisplayUpdate = this.state.allowCookiesUpdate;
    } else {
      shouldDisplay = !CookieStorageService.get('CAM4AcceptCookieConsent') && !isMobile;
      shouldDisplayUpdate = false;
    }

    const link = `
      <a
        class=${styles.promptLink}
        href=${routes.cookies}
        target="_blank"
        id=${this.makeId(this.IDS.cookieLink)}
      >
    `;
    const linkLoggedIn = `
      <a
        class=${styles.promptLink}
        href=${routes.get(routes.accountPrivacy, { user: user.username })}
        target="_blank"
        id=${this.makeId(this.IDS.settingsLink)}
      >
    `;
    const cookiesStyles = {
      display: shouldDisplayUpdate ? 'block' : 'none',
      bottom: shouldDisplay ? '170px' : '5px',
      maxWidth: shouldDisplay ? '485px' : '440px',
    };

    return (
      <>
        {' '}
        {user?.username && (
          <div className={styles.wrapper} style={cookiesStyles}>
            <span className={styles.promptCookie} />
            <div className={styles.promptText}>
              {translate('disclaimer.disclaimer.cookies.text6', {
                0: link,
                1: '</a>',
              })}
            </div>
            <Button
              className={styles.button}
              colour="broadcasterBlack"
              id={this.makeId(this.IDS.buttonUpdate)}
              size="small"
              onClick={this.onAcceptCookiesUpdate}
            >
              {translate('disclaimer.disclaimer.cookies.okay')}
            </Button>
          </div>
        )}
        <div className={styles.wrapper} style={{ display: shouldDisplay ? 'block' : 'none' }}>
          <span className={styles.promptCookie} />
          <div className={styles.promptText}>
            {user.username ? translate('disclaimer.disclaimer.cookies.text5', {
              0: linkLoggedIn,
              1: '</a>',
              2: link,
              3: '</a>',
            }) : translate('disclaimer.disclaimer.cookies.text4', {
              0: link,
              1: '</a>',
            })}
          </div>
          <Button
            className={styles.button}
            colour="broadcasterBlack"
            id={this.makeId(this.IDS.button)}
            size="small"
            onClick={this.onAcceptCookies}
          >
            {translate('disclaimer.disclaimer.cookies.button')}
          </Button>
        </div>
      </>
    );
  }
}

export default CookieConsent;
